import type {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  ReactNode,
  Ref,
} from 'react'
import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react'
import { useSyncRefs } from '../hooks/useSyncRefs'
import Menu from '../Menu/Menu'
import type SvgIcon from '../SvgIcon/SvgIcon'
import type {
  TextInputColor,
  TextInputSize,
  TextInputVariant,
} from '../TextInput/styles'
import type { Actions } from './Actions'
import { ActionTypes } from './Actions'
import Group from './Group'
import type { OptionProps } from './Option'
import Option from './Option'
import Text from './Text'

type EventActions = 'revert'

type SelectReducers = {
  [ActionType in ActionTypes]: (
    state: SelectState,
    action: Actions,
  ) => SelectState
}

interface SelectState {
  value: string
}

type SelectContextState = SelectState & {
  displayInputRef: MutableRefObject<HTMLInputElement | null>
  inputRef: MutableRefObject<HTMLInputElement | null>
  wrapText?: boolean
}

type UseSelectValues = [SelectContextState, Dispatch<Actions>]

const SelectContext = createContext<
  [SelectContextState, Dispatch<Actions>] | null
>(null)

const reducers: SelectReducers = {
  [ActionTypes.SelectOption]: (
    state,
    action: Extract<Actions, { type: ActionTypes.SelectOption }>,
  ) => ({ ...state, value: action.value }),
}

const runReducer = (state: SelectState, action: Actions): SelectState =>
  reducers[action.type](state, action)

export const useSelect = (): UseSelectValues =>
  useContext(SelectContext) as UseSelectValues

export interface SelectProps {
  /**
   * The color scheme of the select input
   * @default 'default'
   */
  color?: TextInputColor
  /**
   * The initial selected value
   * @default ''
   */
  defaultValue?: string
  /**
   * Custom data attributes to be added to the input element
   */
  dataSet?: Record<string, string | undefined>
  /**
   * If true, the select is disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Whether the select is in an error state
   * @default false
   */
  error?: boolean
  /**
   * Additional descriptive text or error text to display below the select
   */
  helperText?: string
  /**
   * Reference to the hidden input element containing the value of the selection
   */
  inputRef?: Ref<HTMLInputElement>
  /**
   * The label for the select
   */
  label: string
  /**
   * The name attribute for the input element
   */
  name?: string
  /** Callback fired when the selected value changes.
   * @param event - The change event
   * @param actions - An object containing action functions like `revert`
   */
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    actions: Record<EventActions, () => void>,
  ) => void
  /**
   * The size of the select input
   * @default 'medium'
   */
  size?: TextInputSize
  /**
   * Icon or another element to display at the start of the input
   */
  startAdornment?: typeof SvgIcon | ReactNode
  /**
   * The visual style variant of the select
   * @default 'outlined'
   */
  variant?: TextInputVariant
  /**
   * Whether option text should wrap to multiple lines instead of truncating
   * @default false
   */
  wrapText?: boolean
}

function Select({
  children,
  color = 'default',
  dataSet,
  defaultValue = '',
  disabled = false,
  error = false,
  helperText,
  inputRef,
  label,
  name,
  onChange,
  size = 'medium',
  startAdornment,
  variant = 'outlined',
  wrapText = false,
}: PropsWithChildren<SelectProps>): JSX.Element {
  const displayInputRef = useRef<HTMLInputElement>(null)
  const localRef = useRef<HTMLInputElement>(null)
  const syncRef = useSyncRefs<HTMLInputElement | null>(localRef, inputRef)

  const [state, dispatch] = useReducer(runReducer, {}, () => ({
    value: defaultValue,
  }))

  useEffect(() => {
    // When options do not contain an empty option, display value should be adjusted manually
    if (state.value === '' && displayInputRef.current) {
      displayInputRef.current.value = ''
    }
  }, [state.value])

  const revert = () => {
    const currentState = { ...state }

    return () => {
      const input = localRef.current

      setTimeout(() => {
        dispatch({ type: ActionTypes.SelectOption, ...currentState })
      }, 1)

      if (input) {
        input.value = currentState.value
      }
    }
  }

  return (
    <SelectContext.Provider
      value={[
        { ...state, displayInputRef, inputRef: localRef, wrapText },
        dispatch,
      ]}
    >
      <Menu>
        <Text
          color={color}
          dataSet={dataSet}
          defaultValue={defaultValue}
          disabled={disabled}
          displayInputRef={displayInputRef}
          error={error}
          helperText={helperText}
          inputRef={syncRef}
          label={label}
          name={name}
          onChange={onChange}
          revert={revert}
          size={size}
          startAdornment={startAdornment}
          variant={variant}
        />
        <Menu.Items>{children}</Menu.Items>
      </Menu>
    </SelectContext.Provider>
  )
}

Select.Option = forwardRef<HTMLButtonElement, PropsWithChildren<OptionProps>>(
  Option,
)
Select.Group = Group

export default Select
